import React from 'react';
import { Link2, ArrowRight, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import LoggedInUserWrapper from './core/LoggedInUserWrapper';
import { Button } from './shared';
import { useAuth } from '../hooks';
import { useToast } from '../hooks/useToast';
import { isValidClasswiseAssignmentURL, isValidStartWithDetailsURL } from '../utils/helper';
import { getAssignmentStartUrl } from '../utils/urlHelpers';
import { Card } from './ui/card';

const JoinPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const linkRef = React.useRef('');
  const navigate = useNavigate();
  const [error, setError] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);

  const firstName = user?.displayName?.split(' ')?.[0] ?? 'there';

  const handleJoin = () => {
    const link = linkRef.current.value?.trim();
    setError('');

    if (!link) {
      setError('Please paste the assignment link shared by your teacher.');
      return;
    }

    if (isValidStartWithDetailsURL(link)) {
      const assignmentId = link.split('/').pop();
      navigate(`/start-details/${assignmentId}`);
      return;
    }

    if (!isValidClasswiseAssignmentURL(link)) {
      setError('That doesn\'t look like a valid Classwise assignment link. Check with your teacher.');
      toast({
        title: 'Invalid link',
        description: 'Please enter a valid assignment link shared with you.',
        status: 'error',
        isClosable: true
      });
    } else {
      const assignmentId = link.split('/').pop();
      navigate(getAssignmentStartUrl({ assignmentId }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleJoin();
  };

  return (
    <LoggedInUserWrapper>
      <div className="cw-page flex min-h-[70vh] items-center justify-center">
        <div className="w-full max-w-xl">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
              <Link2 className="h-7 w-7 text-primary" aria-hidden />
            </div>
            <h1 className="text-2xl font-bold text-foreground md:text-3xl">
              Join an Assignment
            </h1>
            <p className="mt-2 text-muted-foreground">
              Hi {firstName}, paste the link your teacher shared to get started.
            </p>
          </div>

          <Card className="border-border/60 p-6 shadow-sm md:p-8">
            <label htmlFor="assignment-link" className="block text-sm font-medium text-foreground">
              Assignment link
            </label>
            <div
              className={`mt-2 flex flex-col gap-3 sm:flex-row ${
                isFocused ? 'ring-2 ring-ring/30 rounded-lg' : ''
              }`}
            >
              <input
                id="assignment-link"
                ref={linkRef}
                type="url"
                placeholder="Enter the assignment link shared by your teacher"
                className={`cw-input flex-1 ${error ? 'border-destructive/50 focus-visible:ring-destructive/30' : ''}`}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onKeyDown={handleKeyDown}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? 'join-error join-help' : 'join-help'}
              />
              <Button
                onClick={handleJoin}
                className="h-11 shrink-0 gap-2 px-6 sm:w-auto w-full"
              >
                Join
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Button>
            </div>

            {error && (
              <p id="join-error" className="mt-2 text-sm text-destructive" role="alert">
                {error}
              </p>
            )}

            <div
              id="join-help"
              className="mt-4 flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground"
            >
              <HelpCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <p>
                Your teacher will share a link via email, WhatsApp, or your classroom portal. Copy
                the full URL and paste it above.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </LoggedInUserWrapper>
  );
};

export default JoinPage;
